import { toast } from 'sonner';
import { authFetch } from '@/lib/auth-fetch';

const API_BASE_URL = import.meta.env.VITE_LIVE_API_BASE_URL ?? 'http://localhost:5000/api';

export class ApiError extends Error {
  public status: number;
  public statusText: string;

  constructor(status: number, statusText: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.title || errorMessage;
    } catch {
    }
    
    throw new ApiError(response.status, response.statusText, errorMessage);
  }
  
  if (response.status === 204) {
    return {} as T;
  }
  
  try {
    return await response.json();
  } catch (error) {
    throw new Error('Failed to parse response JSON');
  }
}

export const apiClient = {
  async get<T>(endpoint: string): Promise<T> {
    const response = await authFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await handleResponse<T>(response);
  },

  async post<T, D = unknown>(endpoint: string, data: D): Promise<T> {
    const response = await authFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return await handleResponse<T>(response);
  },

  async put<T, D = unknown>(endpoint: string, data: D): Promise<T> {
    const response = await authFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return await handleResponse<T>(response);
  },

  async delete<T>(endpoint: string): Promise<T> {
    const response = await authFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await handleResponse<T>(response);
  },
};
